import { Check, ChevronDown, FilePlus2, LayoutTemplate, Pencil, Save, Trash2, Users } from 'lucide-react'

import { Button } from 'components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { cn } from 'lib/utils'

import type { ExplorerReport } from '../types/explorer.types'

interface SavedReportPickerProps {
	reports: ExplorerReport[]
	loadedReport: ExplorerReport | null
	/** True when the working layout has diverged from the loaded report. */
	dirty: boolean
	hasColumns: boolean
	isSaving?: boolean
	onOpen: (report: ExplorerReport) => void
	onNew: () => void
	onSave: () => void
	onSaveAs: () => void
	onRename: () => void
	onDelete: () => void
	/** File the loaded report as a template (MYD-598). Omit to hide the item. */
	onSaveAsTemplate?: () => void
}

/**
 * Report picker and save controls (MYD-363).
 *
 * The dirty state is the whole point of this component. Without it a user
 * cannot tell whether what they are looking at is the report they opened or an
 * unsaved variation of it, and "Save" becomes a guess about which one they are
 * about to overwrite.
 */
export function SavedReportPicker({
	reports,
	loadedReport,
	dirty,
	hasColumns,
	isSaving = false,
	onOpen,
	onNew,
	onSave,
	onSaveAs,
	onRename,
	onDelete,
	onSaveAsTemplate,
}: SavedReportPickerProps) {
	const label = loadedReport ? loadedReport.name : 'Unsaved report'

	return (
		<div className="flex items-center gap-2">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" isOutline className="max-w-[260px]">
						<span className="truncate">{label}</span>
						{/* An asterisk rather than the word "unsaved": it sits inside a
						    fixed-width control next to a name that is already truncated. */}
						{dirty && loadedReport && (
							<span className="ml-1 text-primary" title="Unsaved changes">
								*
							</span>
						)}
						<ChevronDown className="w-4 h-4 ml-2 shrink-0" />
					</Button>
				</DropdownMenuTrigger>

				<DropdownMenuContent align="start" className="w-64">
					<DropdownMenuItem onClick={onNew}>
						<FilePlus2 className="w-4 h-4 mr-2" />
						New report
					</DropdownMenuItem>

					{reports.length > 0 && <DropdownMenuSeparator />}

					{reports.map(report => (
						<DropdownMenuItem key={report.id} onClick={() => onOpen(report)}>
							<Check
								className={cn(
									'w-4 h-4 mr-2 shrink-0',
									report.id === loadedReport?.id ? 'text-primary' : 'text-transparent',
								)}
							/>
							<span className="truncate">{report.name}</span>
							{/* Someone else's shared layout. Worth marking: the rows are
							    still yours, but the report is not. */}
							{!report.isOwner && (
								<Users className="w-3.5 h-3.5 ml-auto shrink-0 text-gray-400" />
							)}
						</DropdownMenuItem>
					))}

					{/* Any report you can open, a partner's shared one included: the
					    template is a copy filed under your name. It copies what was
					    SAVED, so unsaved changes have to be saved first rather than
					    silently left out of the template. */}
					{loadedReport && onSaveAsTemplate && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								onClick={dirty ? undefined : onSaveAsTemplate}
								disabled={dirty}
								title={dirty ? 'Save your changes first' : undefined}
							>
								<LayoutTemplate className="w-4 h-4 mr-2" />
								Save as template
								{dirty && <span className="ml-auto pl-2 text-xs text-gray-400">save first</span>}
							</DropdownMenuItem>
						</>
					)}

					{loadedReport?.isOwner && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={onRename}>
								<Pencil className="w-4 h-4 mr-2" />
								Rename
							</DropdownMenuItem>
							<DropdownMenuItem onClick={onDelete} className="text-destructive">
								<Trash2 className="w-4 h-4 mr-2" />
								Delete
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Save applies only to a loaded report with actual changes. Offering it
			    on an unchanged report invites a click that does nothing. */}
			{loadedReport?.isOwner && dirty && (
				<Button onClick={onSave} disabled={isSaving}>
					<Save className="w-4 h-4 mr-2" />
					Save
				</Button>
			)}

			{/* Save as covers both "keep a copy" and the first save of an unsaved
			    layout, so a user never has to work out which button applies. */}
			<Button
				variant="ghost"
				isOutline
				onClick={loadedReport ? onSaveAs : onSave}
				disabled={isSaving || !hasColumns}
				title={hasColumns ? undefined : 'Add a column before saving'}
			>
				{loadedReport ? 'Save as' : 'Save report'}
			</Button>
		</div>
	)
}

export default SavedReportPicker
